import React, { useEffect, useState } from "react";
import orderApi from "../../../services/apiServices/orderApi";
import "./PaymentPage.css";
import { setLoading } from "../../../services/redux/loadingSlice";
import { useDispatch } from "react-redux";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { useNavigate } from "react-router-dom";
import ArrowIcon from "../../../components/icons/ArrowIcon";
import { useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { maskDocument, maskCreditCard } from "../../../components/masks/masks";
import { cardBrandCodeMap } from "../../../utils/cardBrand";
import paymentApi from "../../../services/apiServices/paymentApi";
import configService from "../../../services/configService";

const PaymentPage = () => {
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [isCardValid, setIsCardValid] = useState(false);
    const [isCardValidating, setIsCardValidating] = useState(false);
    const [cardBrandApi, setCardBrandApi] = useState(null);
    const [cardValidationError, setCardValidationError] = useState("");
    const [openPaymentMethod, setOpenPaymentMethod] = useState("pix");
    const [formData, setFormData] = useState({
        creditCardNumber: "",
        securityCode: "",
        expirationMonth: "",
        expirationYear: "",
        cardHolderCpf: "",
        cardHolderName: "",
        installments: ""
    });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const isDigitalOrder = String(order?.IsDigital) === "1" || order?.IsDigital === true;
    const termsOfPurchaseUrl = configService.getTermsOfPurchasePdfUrl ? configService.getTermsOfPurchasePdfUrl() : "/termo-de-compra.pdf";

    const handleTogglePaymentMethod = (method) => {
        setOpenPaymentMethod((prev) => (prev === method ? null : method));
    };

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { number } = useParams();

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                dispatch(setLoading(true));
                const response = number ? await orderApi.getOrderByNumber(number, {include: 'Customer.User.Address.City'}) : await orderApi.GetOrderForClient({include: 'Customer.User.Address.City'});
                setOrder(response);
            } catch (err) {
                console.error("Error fetching order:", err);
                setError("Erro ao carregar pedido. Tente novamente.");
            } finally {
                dispatch(setLoading(false));
            }
        };

        fetchOrder();
    }, [dispatch, number]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [id]: value
        }));
    };

    const mapApiBrandToKey = (brand) => {
        const normalized = String(brand || "").toLowerCase();
        if (normalized.includes("visa")) return "VISA";
        if (normalized.includes("master")) return "MASTERCARD";
        if (normalized.includes("american")) return "AMEX";
        if (normalized.includes("elo")) return "ELO";
        if (normalized.includes("diners")) return "DINERS";
        if (normalized.includes("discover")) return "DISCOVER";
        if (normalized.includes("hiper")) return "HIPERCARD";
        if (normalized.includes("jcb")) return "JCB";
        return "CREDIT_CARD";
    };

    const getBrandDisplayName = (brand) => {
        const key = mapApiBrandToKey(brand);
        switch (key) {
            case "AMEX":
                return "American Express";
            case "VISA":
                return "Visa";
            case "MASTERCARD":
                return "Mastercard";
            case "ELO":
                return "Elo";
            case "DINERS":
                return "Diners Club";
            case "DISCOVER":
                return "Discover";
            case "HIPERCARD":
                return "Hipercard";
            case "JCB":
                return "JCB";
            default:
                return brand || "";
        }
    };

    useEffect(() => {
        const digits = String(formData.creditCardNumber || "").replace(/\D/g, "");
        setIsCardValid(false);
        setCardBrandApi(null);
        setCardValidationError("");

        if (!digits) {
            setIsCardValidating(false);
            return;
        }

        if (digits.length < 13) {
            // Not enough digits to validate
            setIsCardValidating(false);
            return;
        }

        let cancelled = false;
        setIsCardValidating(true);
        (async () => {
            const result = await paymentApi.validateCard(digits);
            if (cancelled) return;
            if (result?.Brand) {
                setIsCardValid(true);
                setCardBrandApi(result.Brand);
            } else {
                setIsCardValid(false);
                setCardValidationError(result?.Errors?.[0] || "Cartão inválido.");
            }
            setIsCardValidating(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [formData.creditCardNumber]);

    useEffect(() => {
        setTermsAccepted(false);
    }, [order?.Id]);

    const handleSubmit = async (e, method) => {
        e.preventDefault();

        if (isDigitalOrder && !termsAccepted) {
            toast.error("É necessário aceitar os termos de compra para continuar.");
            return;
        }

        const isPix = method === "pix";

        if (!isPix) {
            if (!isCardValid || !cardBrandApi) {
                toast.error("Valide o número do cartão antes de continuar.");
                return;
            }
        }

        let paymentMethod;

        if (isPix) {
            paymentMethod = cardBrandCodeMap.PIX;
        } else {
            const brandKey = mapApiBrandToKey(cardBrandApi);
            paymentMethod = cardBrandCodeMap[brandKey] || 0;
        }

        const deviceData = {
            ColorDepth: window.screen.colorDepth,
            DeviceFingerprint: null,
            DeviceType3ds: "BROWSER",
            JavaEnabled: navigator.javaEnabled(),
            Language: navigator.language || "BR",
            ScreenHeight: window.screen.height,
            ScreenWidth: window.screen.width,
            TimeZoneOffset:
                (new Date().getTimezoneOffset() / -60) < 0
                    ? -1 * (new Date().getTimezoneOffset() / -60)
                    : (new Date().getTimezoneOffset() / -60),
            UserAgent: navigator.userAgent
        };

        const requestBody = {
            orderId: order.Id.toString(),
            card: String(formData.creditCardNumber || "").replace(/\D/g, ""),
            cvv: formData.securityCode,
            year: '20' + formData.expirationYear,
            month: formData.expirationMonth,
            document: formData.cardHolderCpf,
            name: formData.cardHolderName,
            installments: formData.installments,
            paymentMethod: paymentMethod,
            addressId: address.Id,
            ...deviceData
        };

        try {
            dispatch(setLoading(true));
            await orderApi.createOrder(requestBody);
            if(paymentMethod === cardBrandCodeMap.PIX)
                toast.success("Pedido realizado com sucesso!");
            else
                toast.success("Pagamento realizado com sucesso!");

            navigate('/pedido/resultado/' + order.NrOrder);
        } catch (err) {
            toast.error(err?.response?.data?.Message ?? "Erro ao processar pagamento. Tente novamente.");
        } finally {
            dispatch(setLoading(false));
        }
    };

    if (error) {
        return <p className="payment-error-message">{error}</p>;
    }

    if (!order) {
        return <p className="payment-loading-message">Carregando...</p>;
    }

    const items = JSON.parse(order.StItems);
    const address = order.Customer?.User?.Address?.[0];
    const renderTermsOfPurchase = () => {
        if (!isDigitalOrder) return null;
        return (
            <div className="form-group terms-of-purchase">
                <div className="terms-checkbox">
                    <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span>
                        Li e aceito os{" "}
                        <a href={termsOfPurchaseUrl} target="_blank" rel="noopener noreferrer">
                            termos de compra
                        </a>.
                    </span>
                </div>
            </div>
        );
    };

    const paymentAvailable = (order) => {
        return order.OrderStatusId === 0 && order.LinkCode !== null;
    };

    return (
        <div className="payment-page">
            <span className="div-center clickable link small-font right-component" onClick={() => navigate('/')}> <ArrowIcon size={12}/> Voltar para a lista de compras</span>
            <PageTitle title="Resumo da compra" />
            <div className="payment-div">
                <div className="payment-summary">
                    <h3 className="title">Resumo da compra</h3>
                    <p>
                        <strong>{items.length} itens</strong> 
                    </p>
                    <p className="payment-summary-total">
                        <strong>Total do pedido</strong> 
                        <span>R$ {order.VlnetPrice.toFixed(2).replace(".", ",")}</span>
                    </p>
                </div>
            </div>

            <div className="payment-div">
                <h3 className="title">Itens</h3>
                <div className="payment-items">
                    {items.map((item, index) => (
                        <div key={index} className="payment-item">
                            {item.ISBN ? (
                                <img
                                    src={`https://pwm-public-product-pro.s3.amazonaws.com/${item.ISBN}-large.webp`}
                                    alt="Produto"
                                    className="order-item-image"
                                />
                            ) : (
                                <img
                                    src="https://pwm-public-static-pro.s3.amazonaws.com/static/img/no-cover.png"
                                    alt="Produto"
                                    className="order-item-image"
                                />
                            )}
                            <div className="payment-item-details">
                                <h4>{item.Description}</h4>
                                <p>
                                    <strong>Qtd.</strong> {item.Quantity}{" "}
                                    <span>R$ {item.UnitPrice.toFixed(2).replace(".", ",")}</span>
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {address && (
                <div className="payment-div">
                    <h3 className="title">Endereço</h3>
                    <div className="payment-address-info">
                        <p>
                            {address.StStreet}, {address.NrBuildIngsite}
                            {address.StComplement && `, ${address.StComplement}`} -{" "}
                            {address.StNeighborhood}
                        </p>
                        <p>
                            {address.City ? `${address.City.Name} - ${address.City.State}` : ""}
                        </p>
                        <p>{address.StZipCode}</p>
                    </div>
                </div>
            )}

            <div className="payment-div">
                <h3 className="title">Formas de Pagamento:</h3>
                {order.IsPixEnabled && (
                    <div className={`payment-method ${openPaymentMethod === "pix" ? "open" : ""}`} id="pix">
                        <div className="payment-method-header" onClick={() => handleTogglePaymentMethod("pix")}>
                            <h4>Pix</h4>
                            <span className="payment-method-arrow">
                                {openPaymentMethod === "pix" ? "▲" : "▼"}
                            </span>
                        </div>
                        {openPaymentMethod === "pix" && (
                            <div className="payment-method-body">
                                <form className="credit-card-form" onSubmit={(e) => handleSubmit(e, "pix")}>
                                    <div className="form-group" id="qrcode">
                                        <p className="pix-title">Pagamento à vista: R$ {order.VlnetPrice.toFixed(2).replace(".", ",")}</p>
                                        <ul className="pix-instructions">
                                            <li><strong>Expiração: </strong> ao clicar em "Confirmar Pedido" você terá 15 minutos para realizar o pagamento através da forma de pagamento PIX.</li>
                                            <li><strong>Prazo de entrega: </strong> é contado a partir da confirmação de pagamento pelo banco.</li>
                                            <li><strong>Pagamento: </strong> pode ser feito pelo computador ou dispositivo móvel, utilizando o QR Code gerado.</li>
                                        </ul>
                                    </div>
                                    {renderTermsOfPurchase()}
                                    <div className="margin-top-default right-component">
                                        <button type="submit" className="main-button" disabled={(isDigitalOrder && !termsAccepted) || paymentAvailable(order)}>
                                            <span>Confirmar Pedido</span>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                )}
                <div className={`payment-method ${openPaymentMethod === "card" ? "open" : ""}`} id="cartao">
                    <div className="payment-method-header" onClick={() => handleTogglePaymentMethod("card")}>
                        <h4>Cartão de crédito</h4>
                        <span className="payment-method-arrow">
                            {openPaymentMethod === "card" ? "▲" : "▼"}
                        </span>
                    </div>

                    {openPaymentMethod === "card" && (
                        <div className="payment-method-body">
                            <form className="credit-card-form" onSubmit={(e) => handleSubmit(e, "card")}>
                                <div className="form-group">
                                    <label htmlFor="creditCardNumber">Número do cartão de crédito</label>
                                    <input
                                        type="text"
                                        id="creditCardNumber"
                                        className="main-input"
                                        placeholder="Digite o número do cartão"
                                        required
                                        value={maskCreditCard(formData.creditCardNumber)}
                                        onChange={handleChange}
                                    />
                                    {isCardValidating && (
                                        <span className="small-font">Validando cartão...</span>
                                    )}
                                    {!isCardValidating && !isCardValid && cardValidationError && (
                                        <span className="small-font warning">{cardValidationError}</span>
                                    )}
                                    {!isCardValidating && isCardValid && cardBrandApi && (
                                        <span className="small-font">Bandeira: {getBrandDisplayName(cardBrandApi)}</span>
                                    )}
                                </div>
                                <div className="form-group-row">
                                    <div className="form-group">
                                        <label htmlFor="expirationMonth">Validade</label>
                                        <div className="expiration-date">
                                            <input
                                                type="text"
                                                id="expirationMonth"
                                                className="main-input small-input"
                                                placeholder="MM"
                                                maxLength="2"
                                                required
                                                value={formData.expirationMonth}
                                                onChange={handleChange}
                                                disabled={!isCardValid}
                                            />
                                            <span>/</span>
                                            <input
                                                type="text"
                                                id="expirationYear"
                                                className="main-input small-input"
                                                placeholder="AA"
                                                maxLength="2"
                                                required
                                                value={formData.expirationYear}
                                                onChange={handleChange}
                                                disabled={!isCardValid}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="securityCode">Código de segurança</label>
                                        <input
                                            type="text"
                                            id="securityCode"
                                            className="main-input small-input"
                                            placeholder="CVV"
                                            maxLength={mapApiBrandToKey(cardBrandApi) === "AMEX" ? 4 : 3}
                                            required
                                            value={formData.securityCode}
                                            onChange={handleChange}
                                            disabled={!isCardValid}
                                        />
                                    </div>
                                    <div className="form-group form-group-installments">
                                        <label htmlFor="installments">Parcelas</label>
                                        <select
                                            id="installments"
                                            className="main-input"
                                            required
                                            value={formData.installments}
                                            onChange={handleChange}
                                            disabled={!isCardValid}
                                        >
                                            <option value="">Selecione</option>
                                            <option value="1">1x</option>
                                            <option value="2">2x</option>
                                            <option value="3">3x</option>
                                            <option value="4">4x</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="cardHolderCpf">CPF/CNPJ do titular do cartão</label>
                                    <input
                                        type="text"
                                        id="cardHolderCpf"
                                        className="main-input"
                                        placeholder="Digite o CPF ou CNPJ"
                                        maxLength={18}
                                        required
                                        value={maskDocument(formData.cardHolderCpf)}
                                        onChange={handleChange}
                                        disabled={!isCardValid}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="cardHolderName">Nome do titular do cartão</label>
                                    <input
                                        type="text"
                                        id="cardHolderName"
                                        className="main-input"
                                        placeholder="Digite o nome completo"
                                        required
                                        value={formData.cardHolderName}
                                        onChange={handleChange}
                                        disabled={!isCardValid}
                                    />
                                </div>
                                {renderTermsOfPurchase()}
                                <div className="margin-top-default right-component">
                                    <button type="submit" className="main-button" disabled={!isCardValid || paymentAvailable(order) || (isDigitalOrder && !termsAccepted)}>
                                        <span>Confirmar Pedido</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;
